import React, { useMemo } from 'react';
import { Course, Tag, TagType } from '../../apis/api-client';
import { CourseAutocomplete, SectionHeader, TagAutocomplete } from '../common';
import { courseTypeMeta } from '../courses/contentTypeMeta';
import { CloseIcon, InfoIcon, SearchIcon } from '../icons';
import { Avatar, Box, Button, Chip, CircularProgress, Divider, FormControlLabel, IconButton, Switch, Tooltip, Typography } from '../ui';

interface GroupAccessTabProps {
  // Controlled — grants live in the page (full-replace buildGroupAccessRequest
  // bundles them with seat/branding state).
  customizeContent: boolean;
  setCustomizeContent: (value: boolean) => void;
  contentTags: Tag[];
  setContentTags: React.Dispatch<React.SetStateAction<Tag[]>>;
  provisionedContent: Course[];
  setProvisionedContent: React.Dispatch<React.SetStateAction<Course[]>>;
  // Parent-workspace grant sets — the group's picks must be a subset.
  workspaceTagIds: Set<number>;
  workspaceCourseIds: Set<number>;
  workspaceName?: string;
  saving: boolean;
  onSave: () => void;
}

/**
 * GroupAccessTab
 *
 * The Access tab for the group editor: an inherit-vs-customize toggle, then
 * (when customized) tag rules + provisioned-content grants constrained to
 * the parent workspace's set. Picks outside that set are flagged inline
 * (red) and dropped on save. Extracted from GroupEdit as a CONTROLLED
 * component; the workspace-scope constraint is specific to this surface, so
 * it does NOT share the workspace's Access tab.
 */
export default function GroupAccessTab({
  customizeContent,
  setCustomizeContent,
  contentTags,
  setContentTags,
  provisionedContent,
  setProvisionedContent,
  workspaceTagIds,
  workspaceCourseIds,
  workspaceName,
  saving,
  onSave,
}: GroupAccessTabProps) {
  // When the workspace has no rules at all (size 0), inheritance means
  // "everything's available", so we don't restrict the picker.
  const hasWorkspaceTagRules = workspaceTagIds.size > 0;
  const hasWorkspaceCourseRules = workspaceCourseIds.size > 0;

  // Picks outside the parent workspace's set — flagged inline and dropped
  // on save (the autocompletes can't fully enforce it at pick time).
  const tagsOutOfScope = useMemo(
    () => contentTags.filter((t) => hasWorkspaceTagRules && t.id != null && !workspaceTagIds.has(t.id)),
    [contentTags, workspaceTagIds, hasWorkspaceTagRules],
  );
  const coursesOutOfScope = useMemo(
    () =>
      provisionedContent.filter(
        (c) => hasWorkspaceCourseRules && c.id != null && !workspaceCourseIds.has(c.id),
      ),
    [provisionedContent, workspaceCourseIds, hasWorkspaceCourseRules],
  );

  return (
    <Box sx={{ maxWidth: 960, display: 'flex', flexDirection: 'column', gap: 4 }}>
      {/* Inherit-vs-customize toggle. Default is "use workspace content" so
          a fresh group tracks whatever the workspace grants. Flipping on
          opens the pickers; flipping off hides them WITHOUT wiping the
          saved picks (toggling on later restores them). */}
      <Box
        sx={{
          p: 2,
          borderRadius: 2,
          border: '1px solid',
          borderColor: customizeContent ? 'primary.main' : 'divider',
          bgcolor: customizeContent ? 'rgba(var(--brand-rgb), 0.08)' : 'grey.50',
          transition: 'border-color 150ms ease, background-color 150ms ease',
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 2 }}>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography variant="subtitle1" fontWeight={700}>
              Content access
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.25 }}>
              {customizeContent
                ? 'This group has a custom content set. Picks must be a subset of the parent workspace’s grants.'
                : `Inherits from ${workspaceName || 'the parent workspace'}. Members see whatever the workspace grants.`}
            </Typography>
          </Box>
          <FormControlLabel
            control={
              <Switch
                checked={customizeContent}
                onChange={(e) => setCustomizeContent(e.target.checked)}
              />
            }
            label={
              <Typography variant="body2" fontWeight={600}>
                Customize content
              </Typography>
            }
            sx={{ ml: 0, flexShrink: 0 }}
          />
        </Box>
      </Box>

      {/* Out-of-scope warning + customize panels only render when the admin
          has opted in. Saved picks are preserved while hidden. */}
      {customizeContent && (
        <>
          <Box
            sx={{
              display: 'flex',
              alignItems: 'flex-start',
              gap: 1,
              p: 1.5,
              borderRadius: 1,
              bgcolor: 'warning.softBg',
              border: '1px solid #ffe082',
              color: 'warning.softText',
            }}
          >
            <InfoIcon fontSize="small" sx={{ color: 'inherit', mt: 0.25 }} />
            <Typography variant="caption" sx={{ color: 'inherit' }}>
              Group access can only grant content already available to the parent
              workspace. Picks outside the workspace's set are flagged below and
              dropped on save.
            </Typography>
          </Box>

          {/* Content tag rules — out-of-scope tags flagged inline (red). */}
          <Box>
            <SectionHeader
              variant="accent"
              title="Content access by tag"
              subtitle={
                hasWorkspaceTagRules
                  ? `Only tags granted to ${workspaceName || 'this workspace'} are valid for the group.`
                  : 'The workspace has no tag rules — any course tag is valid here.'
              }
            />
            <Box
              sx={{
                mt: 1.5,
                display: 'flex',
                gap: 2.5,
                flexDirection: { xs: 'column', md: 'row' },
                alignItems: { md: 'flex-start' },
              }}
            >
              {/* Left: search-to-add, constrained to the workspace's tags. */}
              <Box sx={{ width: { xs: '100%', md: 320 }, flexShrink: 0 }}>
                <TagAutocomplete
                  value={null}
                  onChange={(v) => {
                    const picked = Array.isArray(v) ? v[0] : v;
                    if (!picked) return;
                    if (contentTags.some((t) => t.id === picked.id)) return;
                    setContentTags((cur) => [...cur, picked]);
                  }}
                  tagType={TagType.CourseTag}
                  // Enforce "group scope ⊆ workspace scope" at the picker.
                  // No workspace rules → unrestricted (no-rules = anything).
                  restrictToIds={hasWorkspaceTagRules ? Array.from(workspaceTagIds) : undefined}
                  placeholder={hasWorkspaceTagRules ? 'Search workspace tags…' : 'Search tags…'}
                  label="Add a tag"
                  helperText={
                    hasWorkspaceTagRules ? 'Limited to tags provisioned to this workspace.' : undefined
                  }
                />
              </Box>

              {/* Right: granted-tags list with out-of-scope marking. */}
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography
                  variant="caption"
                  color="text.secondary"
                  fontWeight={700}
                  sx={{ display: 'block', textTransform: 'uppercase', letterSpacing: '0.04em', mb: 1 }}
                >
                  {contentTags.length > 0
                    ? `${contentTags.length} tag${contentTags.length === 1 ? '' : 's'} granted`
                    : 'No tags granted yet'}
                  {tagsOutOfScope.length > 0 && (
                    <Typography
                      variant="caption"
                      component="span"
                      color="error"
                      sx={{ ml: 1, textTransform: 'none', letterSpacing: 0, fontWeight: 500 }}
                    >
                      · {tagsOutOfScope.length} out of scope
                    </Typography>
                  )}
                </Typography>
                {contentTags.length > 0 ? (
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75 }}>
                    {contentTags.map((t) => {
                      const isOutOfScope =
                        hasWorkspaceTagRules && t.id != null && !workspaceTagIds.has(t.id);
                      return (
                        <Tooltip
                          key={t.id}
                          title={
                            isOutOfScope
                              ? 'Not granted to the parent workspace — will be dropped on save.'
                              : ''
                          }
                          disableHoverListener={!isOutOfScope}
                        >
                          <Chip
                            label={t.name}
                            onDelete={() => setContentTags((cur) => cur.filter((x) => x.id !== t.id))}
                            deleteIcon={<CloseIcon />}
                            sx={{
                              height: 30,
                              px: 0.5,
                              fontWeight: 600,
                              bgcolor: isOutOfScope ? 'rgba(244,67,54,0.10)' : 'rgba(var(--brand-rgb), 0.14)',
                              color: isOutOfScope ? 'error.main' : 'primary.main',
                              border: '1px solid',
                              borderColor: isOutOfScope ? 'rgba(244,67,54,0.45)' : 'rgba(var(--brand-rgb), 0.45)',
                              '& .MuiChip-deleteIcon': {
                                color: isOutOfScope ? 'error.main' : 'primary.main',
                                opacity: 0.7,
                                '&:hover': {
                                  opacity: 1,
                                  color: isOutOfScope ? 'error.dark' : 'primary.dark',
                                },
                              },
                            }}
                          />
                        </Tooltip>
                      );
                    })}
                  </Box>
                ) : (
                  <Box
                    sx={{
                      p: 2,
                      borderRadius: 1.5,
                      border: '1px dashed',
                      borderColor: 'divider',
                      bgcolor: 'grey.50',
                      color: 'text.secondary',
                      textAlign: 'center',
                    }}
                  >
                    <Typography variant="body2">
                      Search for a tag to grant it to this group.
                    </Typography>
                  </Box>
                )}
              </Box>
            </Box>
          </Box>

          <Divider />

          {/* Provisioned content */}
          <Box>
            <SectionHeader
              variant="accent"
              title="Provisioned content"
              subtitle={
                hasWorkspaceCourseRules
                  ? `Pick from the ${workspaceCourseIds.size} item${workspaceCourseIds.size === 1 ? '' : 's'} the workspace has provisioned.`
                  : 'The workspace has no explicit course rules — any course is valid here.'
              }
            />
            <Box sx={{ mt: 1.5 }}>
              <CourseAutocomplete
                value={null}
                onChange={(v) => {
                  const picked = Array.isArray(v) ? v[0] : v;
                  if (!picked) return;
                  if (provisionedContent.some((c) => c.id === picked.id)) return;
                  setProvisionedContent((cur) => [...cur, picked]);
                }}
                placeholder="Search courses, paths, and events…"
                label="Search content"
              />

              {provisionedContent.length > 0 ? (
                <Box sx={{ mt: 2, display: 'flex', flexDirection: 'column', gap: 1 }}>
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    fontWeight={700}
                    sx={{ textTransform: 'uppercase', letterSpacing: '0.04em' }}
                  >
                    {provisionedContent.length} item{provisionedContent.length === 1 ? '' : 's'} selected
                    {coursesOutOfScope.length > 0 && ` · ${coursesOutOfScope.length} out of scope`}
                  </Typography>
                  <Box
                    sx={{
                      border: '1px solid',
                      borderColor: 'divider',
                      borderRadius: 2,
                      overflow: 'hidden',
                    }}
                  >
                    {provisionedContent.map((c, idx) => {
                      const meta = courseTypeMeta(c.courseType);
                      const isOutOfScope =
                        hasWorkspaceCourseRules && c.id != null && !workspaceCourseIds.has(c.id);
                      return (
                        <Box
                          key={c.id}
                          sx={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 1.5,
                            px: 1.5,
                            py: 1,
                            bgcolor: isOutOfScope ? 'rgba(244,67,54,0.06)' : 'background.paper',
                            borderBottom: idx === provisionedContent.length - 1 ? 0 : '1px solid',
                            borderColor: 'divider',
                            '&:hover': { bgcolor: 'action.hover' },
                            transition: 'background-color 120ms ease',
                          }}
                        >
                          <Avatar
                            variant="rounded"
                            sx={{
                              width: 36,
                              height: 36,
                              bgcolor: `${meta.color}18`,
                              color: meta.color,
                              border: '1px solid',
                              borderColor: 'divider',
                              flexShrink: 0,
                            }}
                          >
                            {meta.icon}
                          </Avatar>
                          <Box sx={{ flex: 1, minWidth: 0 }}>
                            <Typography variant="body2" fontWeight={600} noWrap>
                              {c.title || `Course #${c.id}`}
                            </Typography>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mt: 0.25 }}>
                              <Chip
                                label={meta.label}
                                size="small"
                                sx={{
                                  height: 18,
                                  fontSize: '0.65rem',
                                  fontWeight: 600,
                                  bgcolor: `${meta.color}15`,
                                  color: meta.color,
                                  '& .MuiChip-label': { px: 0.75 },
                                }}
                              />
                              {isOutOfScope && (
                                <Chip
                                  label="Out of workspace scope"
                                  size="small"
                                  color="error"
                                  variant="outlined"
                                  sx={{
                                    height: 18,
                                    fontSize: '0.65rem',
                                    fontWeight: 600,
                                    '& .MuiChip-label': { px: 0.75 },
                                  }}
                                />
                              )}
                            </Box>
                          </Box>
                          <Tooltip title="Remove">
                            <IconButton
                              size="small"
                              onClick={() =>
                                setProvisionedContent((cur) => cur.filter((x) => x.id !== c.id))
                              }
                            >
                              <CloseIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </Box>
                      );
                    })}
                  </Box>
                </Box>
              ) : (
                <Box
                  sx={{
                    mt: 2,
                    p: 3,
                    borderRadius: 2,
                    border: '1px dashed',
                    borderColor: 'divider',
                    bgcolor: 'grey.50',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: 1,
                    color: 'text.secondary',
                  }}
                >
                  <SearchIcon fontSize="small" />
                  <Typography variant="body2">
                    No content provisioned to this group yet.
                  </Typography>
                </Box>
              )}
            </Box>
          </Box>
        </>
      )}

      <Button
        variant="contained"
        onClick={onSave}
        disabled={saving}
        sx={{ alignSelf: 'flex-start', px: 3 }}
      >
        {saving ? <CircularProgress size={20} color="inherit" /> : 'Save Access Settings'}
      </Button>
    </Box>
  );
}
